import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { ShopSummary } from '../../api/types.ts';
import { formatMoney } from '../../format.ts';
import { palette } from '../../theme.ts';
import { DetailRow } from './DetailRow.tsx';
import { ShopBillingDialog } from './ShopBillingDialog.tsx';

export interface ShopBillingCardProps {
  shop: ShopSummary;
  canManage: boolean;
  onSaved: () => void;
}

function BillingSetting({
  label,
  value,
  custom,
  defaultValue,
}: Readonly<{
  label: string;
  value: string;
  custom: boolean;
  defaultValue: string;
}>): React.JSX.Element {
  return (
    <Box sx={{ py: 0.25 }}>
      <DetailRow label={label} value={value} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Chip
          size="small"
          label={custom ? 'Custom' : 'Default'}
          color={custom ? 'primary' : 'default'}
          variant={custom ? 'filled' : 'outlined'}
        />
        {custom && (
          <Typography variant="caption" sx={{ color: palette.textSecondary }}>
            Default {defaultValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ShopBillingCard({ shop, canManage, onSaved }: Readonly<ShopBillingCardProps>): React.JSX.Element {
  // NOTE: billing figures stay in the shop's native currency (formatMoney), NOT the
  // AUD display toggle -- this is a contractual amount edited in PGK via the dialog
  // below, so a converted approximate would contradict its own editor and the Config page.
  const [open, setOpen] = useState(false);
  const commissionBp = shop.commission_rate_bp_override ?? shop.commission_rate_bp_default;
  const feeMinor = shop.device_monthly_fee_minor_override ?? shop.device_monthly_fee_minor_default;

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h6">1NL billing</Typography>
        {canManage && (
          <IconButton aria-label="Edit 1NL billing" onClick={() => setOpen(true)} sx={{ minWidth: 44, minHeight: 44 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <BillingSetting
        label="Commission"
        value={`${commissionBp / 100}%`}
        custom={shop.commission_rate_bp_override !== null}
        defaultValue={`${shop.commission_rate_bp_default / 100}%`}
      />
      <BillingSetting
        label="Device fee"
        value={formatMoney(feeMinor, shop.currency_code)}
        custom={shop.device_monthly_fee_minor_override !== null}
        defaultValue={formatMoney(shop.device_monthly_fee_minor_default, shop.currency_code)}
      />
      <ShopBillingDialog open={open} shop={shop} onClose={() => setOpen(false)} onSaved={onSaved} />
    </Paper>
  );
}

export default ShopBillingCard;
